import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, X, Pin, Trash2, Send, Users, Loader2, Mail } from "lucide-react";

const AUDIENCES = [
  { value: "all", label: "everyone" },
  { value: "admins", label: "admins only" },
  { value: "staff", label: "staff only" },
];

const ACCENTS = ["#B08D57", "#8B8A72", "#4A3F36", "#B8A89A", "#A65A4A", "#3D5A4A"];

const isAdmin = (user) => user?.role === "admin";

/** Postcard render of a single message. */
export function MessagePostcard({ message, onDismiss, onDelete, compact = false, canDelete = false }) {
  const accent = message.accent_color || "#B08D57";
  return (
    <article
      data-testid={`postcard-${message.id?.slice(0, 8) || "x"}`}
      className="relative bg-ivory border border-taupe/50 shadow-sm overflow-hidden grid grid-cols-1 sm:grid-cols-5"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      {message.image_path && (
        <div className={`sm:col-span-2 ${compact ? "aspect-[4/3]" : "aspect-[5/4]"} bg-linen overflow-hidden`}>
          <img src={fileUrl(message.image_path)} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className={`${message.image_path ? "sm:col-span-3" : "sm:col-span-5"} p-6 flex flex-col`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="editorial-label" style={{ color: accent }}>
              postcard · from {message.author_name || message.author_email || "the studio"}
            </p>
            <p className="text-[0.65rem] tracking-widest uppercase text-taupe mt-1">
              {new Date(message.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              {message.expires_at ? <> · until {new Date(message.expires_at).toLocaleDateString()}</> : <> · <Pin className="w-3 h-3 inline" /> pinned</>}
              {" · "}{message.audience}
            </p>
          </div>
          {onDismiss && (
            <button onClick={() => onDismiss(message)} data-testid="postcard-dismiss" className="text-taupe hover:text-charcoal p-1" aria-label="dismiss">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="font-serif text-xl lowercase text-charcoal mt-4 leading-relaxed whitespace-pre-wrap flex-1">{message.body}</p>
        {canDelete && onDelete && (
          <div className="mt-4 flex justify-end">
            <button onClick={() => onDelete(message)} data-testid="postcard-delete" className="text-xs editorial-label text-red-700 hover:text-red-900 flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> remove
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

/** Composer form — admin only. */
export function MessageComposer({ onPosted }) {
  const fileRef = useRef(null);
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [accent, setAccent] = useState(ACCENTS[0]);
  const [imagePath, setImagePath] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [posting, setPosting] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagePath(data.path);
      setImagePreview(URL.createObjectURL(file));
    } catch (e) {
      toast.error("could not attach image.");
    } finally {
      setUploadingImg(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!body.trim()) { toast.error("write something first."); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/messages", {
        body: body.trim(),
        audience,
        accent_color: accent,
        image_path: imagePath,
        expires_in_days: pinned ? null : Math.max(1, Number(expiresInDays) || 7),
      });
      toast.success("postcard posted.");
      setBody("");
      setImagePath(null);
      setImagePreview(null);
      setPinned(false);
      setExpiresInDays(7);
      onPosted?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not post.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-linen border border-taupe/50 p-6 sm:p-8" data-testid="message-composer">
      <p className="editorial-label">compose · postcard to the team</p>
      <h2 className="font-serif text-2xl lowercase text-charcoal mt-2 mb-6">a note for the studio.</h2>

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={5}
        placeholder="what's on the studio mood-board today?"
        data-testid="composer-body"
        className="w-full bg-ivory border border-taupe/40 p-4 text-charcoal focus:outline-none focus:border-brass leading-relaxed"
      />

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div>
          <p className="editorial-label mb-2">audience</p>
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map((a) => (
              <button
                type="button"
                key={a.value}
                onClick={() => setAudience(a.value)}
                data-testid={`audience-${a.value}`}
                className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border flex items-center gap-2 ${audience === a.value ? "border-brass text-brass" : "border-taupe/50 text-charcoal hover:border-brass"}`}
              >
                <Users className="w-3 h-3" /> {a.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="editorial-label mb-2">ribbon color</p>
          <div className="flex flex-wrap gap-2" data-testid="composer-accents">
            {ACCENTS.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setAccent(c)}
                aria-label={c}
                className={`w-7 h-7 border ${accent === c ? "border-charcoal ring-2 ring-offset-1 ring-brass" : "border-taupe/40"}`}
                style={{ background: c }}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="editorial-label mb-2">lifecycle</p>
          <label className="flex items-center gap-2 text-xs text-charcoal mb-2">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="composer-pin" />
            <Pin className="w-3 h-3" /> pin · no expiry
          </label>
          {!pinned && (
            <div className="flex items-center gap-2 text-xs text-charcoal">
              <span>auto-expire after</span>
              <input
                type="number"
                min="1"
                max="365"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                data-testid="composer-expiry"
                className="bg-ivory border border-taupe/40 w-16 px-2 py-1 text-charcoal focus:outline-none focus:border-brass"
              />
              <span>days</span>
            </div>
          )}
        </div>

        <div>
          <p className="editorial-label mb-2">postcard image (optional)</p>
          {imagePreview ? (
            <div className="relative w-32 h-24 border border-taupe/40">
              <img src={imagePreview} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => { setImagePath(null); setImagePreview(null); }} className="absolute -top-2 -right-2 bg-ivory border border-taupe p-1">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingImg}
              data-testid="composer-image-upload"
              className="border border-taupe/50 text-charcoal hover:border-brass hover:text-brass px-4 py-2 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
            >
              {uploadingImg ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />} {uploadingImg ? "uploading…" : "attach image"}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="submit"
          disabled={posting || !body.trim()}
          data-testid="composer-submit"
          className="bg-brass hover:bg-espresso text-ivory px-6 py-3 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
        >
          {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} post postcard
        </button>
      </div>
    </form>
  );
}

/** Dashboard feed widget — last few messages. */
export function MessageFeed({ user, limit = 4 }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/messages");
      setMessages(data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("messages load failed", e);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm("remove this postcard?")) return;
    try {
      await api.delete(`/messages/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("postcard removed.");
    } catch { toast.error("could not remove."); }
  };

  const visible = messages.slice(0, limit);

  if (loading) {
    return <p className="text-xs text-taupe">loading message board…</p>;
  }

  return (
    <section data-testid="message-feed">
      <div className="flex items-end justify-between mb-4">
        <h2 className="font-serif text-3xl lowercase text-charcoal flex items-center gap-3"><Mail className="w-5 h-5 text-brass" /> the message board</h2>
        <Link to="/admin/messages" className="editorial-label hover:text-brass">view all →</Link>
      </div>
      {visible.length === 0 ? (
        <div className="border border-dashed border-taupe/50 p-8 text-center bg-linen/50">
          <p className="font-serif text-xl lowercase text-taupe">no postcards yet.</p>
          {isAdmin(user) && <p className="text-xs text-espresso mt-2">head to <Link to="/admin/messages" className="text-brass underline">message board</Link> to write the first one.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {visible.map((m) => (
            <MessagePostcard
              key={m.id}
              message={m}
              compact
              canDelete={isAdmin(user)}
              onDelete={remove}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** Login popup — appears on first dashboard hit if there are unread messages. */
export function MessagePopup({ user }) {
  const [unread, setUnread] = useState([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!user) return;
    const sessionKey = `mp_msg_popup_seen_${user.id}`;
    if (sessionStorage.getItem(sessionKey)) return;
    api.get("/messages/unread").then((r) => {
      if (r.data?.length) {
        setUnread(r.data);
        setOpen(true);
        sessionStorage.setItem(sessionKey, "1");
      }
    }).catch((e) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("unread messages fetch failed", e);
      }
    });
  }, [user]);

  const ackCurrent = async () => {
    const m = unread[idx];
    if (!m) return;
    try {
      await api.post(`/messages/${m.id}/ack`);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("message ack failed", e);
      }
    }
    if (idx + 1 < unread.length) {
      setIdx(idx + 1);
    } else {
      setOpen(false);
    }
  };

  const closeAll = async () => {
    // ack all remaining
    for (let i = idx; i < unread.length; i++) {
      try {
        await api.post(`/messages/${unread[i].id}/ack`);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("message ack failed", e);
        }
      }
    }
    setOpen(false);
  };

  if (!open || !unread.length) return null;
  const current = unread[idx];

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="message-popup" onClick={closeAll}>
      <div className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3 text-ivory">
          <p className="editorial-label" style={{ color: "#F6F2EB" }}>
            postcard · {idx + 1} of {unread.length}
          </p>
          <button onClick={closeAll} data-testid="popup-close-all" className="text-ivory/70 hover:text-ivory text-xs tracking-[0.2em] uppercase">
            mark all read
          </button>
        </div>
        <MessagePostcard message={current} />
        <div className="flex items-center justify-between mt-4 text-ivory">
          <span className="text-xs editorial-label" style={{ color: "rgba(246,242,235,0.7)" }}>{current.audience} · from {current.author_name || "studio"}</span>
          <button
            onClick={ackCurrent}
            data-testid="popup-ack-next"
            className="bg-brass hover:bg-espresso text-ivory px-6 py-3 text-xs tracking-[0.25em] uppercase"
          >
            {idx + 1 < unread.length ? "next postcard →" : "got it"}
          </button>
        </div>
      </div>
    </div>
  );
}
